import React, { useState, useEffect, useRef, lazy, Suspense  } from 'react';
import { Container, Button, Card, ProgressBar } from 'react-bootstrap';
import NavigationBar from './Navbar';
import Leaderboard from './Leaderboard';
import '../css/Result.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, where, limit, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, messaging } from '../firebase';
import html2canvas from 'html2canvas';
import { getToken } from 'firebase/messaging';
import NotificationPopup from './NotificationPopup'; // Import the new component
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import AdsContainer from './AdsBanner';

const MoreGame = React.lazy(() => import('./MoreGame'));
// const Certificate = lazy(() => import('./Certificate'));

// Function to convert number to ordinal representation
const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"],
        v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Function to get appreciation message based on score
const getAppreciationMessage = (score) => {
  switch(score) {
    case 5:
      return "Quiz Master!";
    case 4:
      return "Great Job!";
    case 3:
      return "Nice Effort!";
    case 2:
      return "Not bad";
    case 1:
      return "Good Try!";
    default:
      return "Keep Trying!";
  }
};

const Result = ({ playerData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [performanceData, setPerformanceData] = useState({
    score: 0,
    total: 0,
    points: 0
  });
  const [rank, setRank] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [quizDate, setQuizDate] = useState(''); // New state for quiz date
  const [quizTitle, setQuizTitle] = useState(''); // New state for quiz title
  const [showNotificationPopup, setShowNotificationPopup] = useState(false); // State for showing the notification popup
  const resultRef = useRef(null);
  const buttonRef = useRef(null);
  // const [showCertificate, setShowCertificate] = useState(true); // State to toggle certificate display
  
  const [streak, setStreak] = useState(0); // State for streak
  const [weekPlayed, setWeekPlayed] = useState([]); // State for week played layout

  const hasNotificationsAllowed = async (playerId) => {
    try {
      const playerRef = doc(db, 'players', playerId);
      const playerSnap = await getDoc(playerRef);
      return playerSnap.data().notificationsAllowed || false;
    } catch (error) {
      console.error('Error checking notifications permission:', error);
      return false;
    }
  };
  
  const saveFCMTokenToFirestore = async (playerId, token) => {
    try {
      const playerRef = doc(db, 'players', playerId);
      await updateDoc(playerRef, {
        fcmToken: token,
        notificationsAllowed: true // Set flag to indicate notifications are allowed
      });
      console.log('FCM token and permission saved successfully');
    } catch (error) {
      console.error('Error saving FCM token:', error);
    }
  };
  
  // Request permission for notifications
  const requestPermission = async () => {
    try {
      // Ensure that the service worker is registered correctly
      const swRegistration = await navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/firebase-messaging-sw.js`);
      
      // Wait for the service worker to become active
      if (swRegistration.active) {
        const token = await getToken(messaging, { 
          vapidKey: 'BH15GrJccIRYzJWkPWfCq90hjGv5crYiskeDN3vLYrVlabsdk-elZUN94Ouaqs55OYLmTJDX-uNlW_771RHTZGI', 
          serviceWorkerRegistration: swRegistration 
        });
        console.log('Notification permission granted and token generated:', token);
  
        if (token) {
          saveFCMTokenToFirestore(playerData.id, token);
          handleCloseNotificationPopup();
        }
      } else {
        console.error('Service worker is not active.');
      }
    } catch (error) {
      console.log('User denied notification permission', error);
    }
  };
  

  const handleCloseNotificationPopup = () => {
    setShowNotificationPopup(false);
  };
  const fetchPerformanceData = async () => {
    try {
      const playerId = playerData.id;
      const q = query(collection(db, 'performances'), where('playerId', '==', playerId), orderBy('date', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const latestPerformance = querySnapshot.docs[0].data();

        // Extract date only
        const quizDate = new Date(latestPerformance.date).toISOString().split('T')[0];

        setPerformanceData({
          score: latestPerformance.score,
          total: latestPerformance.correctCount + latestPerformance.wrongCount,
          points: latestPerformance.points
        });

        setQuizDate(quizDate); // Set date without time
        fetchQuizTitle(quizDate);

        // Fetch the leaderboard data using only the date
        const leaderboardQuery = query(
          collection(db, 'performances'),
          where('date', '>=', quizDate),
          where('date', '<', new Date(new Date(quizDate).getTime() + 86400000).toISOString().split('T')[0]),
          orderBy('points', 'desc')
        );

        const leaderboardSnapshot = await getDocs(leaderboardQuery);
        const leaderboardData = leaderboardSnapshot.docs.map(doc => doc.data());

        if (leaderboardData.length > 0) {
          // Sort the leaderboard by points in descending order
          const sortedLeaderboard = leaderboardData.sort((a, b) => b.points - a.points);

          const playerRank = sortedLeaderboard.findIndex(player => player.playerId === playerId) + 1;

          setRank(playerRank);
        } else {
          console.error('Leaderboard data is empty or not correctly sorted.');
          setRank(null);
        }

        const playerRef = doc(db, 'players', playerId);
        const playerSnap = await getDoc(playerRef);
        const totalPoints = playerSnap.data().totalPoints || 0;
        
        setTotalPoints(totalPoints);
        

        const notificationsAllowed = await hasNotificationsAllowed(playerId);
        if (!notificationsAllowed) {
          setTimeout(() => {
            setShowNotificationPopup(true);
          }, 5000); // 5-second delay before showing the popup
        }
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    }
  };

  const fetchStreakAndWData = async () => {
    try {
      const playerId = playerData.id;
      const playerRef = doc(db, 'players', playerId);
      const playerSnap = await getDoc(playerRef);
      const playedDates = playerSnap.data().quizPlayedToday || [];
      
      const updatedStreak = calculateStreak(playedDates);
      const updatedWeekPlayed = getWeekPlayedDays(playedDates);

      setStreak(updatedStreak);
      setWeekPlayed(updatedWeekPlayed);

      return updatedStreak; // Return streak value
    } catch (error) {
      console.error('Error fetching Streak And WData:', error);
      return 0; // Return 0 to indicate failure
    }
  };

  const calculateStreak = (dates) => {
    // Sort dates in descending order
    dates.sort((a, b) => new Date(b) - new Date(a));
  
    let streak = 0;
    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
    let lastDate = null;
  
    for (let i = 0; i < dates.length; i++) {
      const currentDate = new Date(dates[i]).toISOString().split('T')[0];
  
      if (currentDate === today) {
        streak = 1; // Today is part of the streak
        lastDate = new Date(today);
      } else if (lastDate) {
        const previousDate = new Date(lastDate);
        previousDate.setDate(previousDate.getDate() - 1);
  
        if (currentDate === previousDate.toISOString().split('T')[0]) {
          streak++;
          lastDate = new Date(currentDate);
        } else {
          break; // Streak is broken
        }
      }
    }
  
    return streak;
  };
  
// Function to get the days of the week the user played
const getWeekPlayedDays = (dates) => {
  const weekPlayed = Array(7).fill(false); // Array for days of the week (Sunday to Saturday)
  const today = new Date();
  
  // Get the start of the current week (Sunday)
  const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
  startOfWeek.setHours(0, 0, 0, 0); // Set time to start of the day

  // Get the end of the current week (Saturday)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999); // Set time to end of the day

  dates.forEach(date => {
    const quizDay = new Date(date);
    if (quizDay >= startOfWeek && quizDay <= endOfWeek) {
      const dayOfWeek = quizDay.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      weekPlayed[dayOfWeek] = true;
    }
  });

  return weekPlayed;
};

  const fetchQuizTitle = async (date) => {
    try {
      // Assuming date is stored as YYYY-MM-DD in Firestore
      const formattedDate = date.split('T')[0]; // Remove time if present
      console.log('Formatted date:', formattedDate);
      const q = query(collection(db, 'quizzes'), where('publicationDate', '==', formattedDate), limit(1));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const quizData = querySnapshot.docs[0].data();
        setQuizTitle(quizData.quizTitle);
        console.log('Fetched Quiz Title:', quizData.quizTitle); // Log the fetched title
      } else {
        console.log('No quiz found for the given date');
      }
    } catch (error) {
      console.error('Error fetching quiz title:', error);
    }
  };

   useEffect(() => {
    const fetchData = async () => {
      const retryDelay = 2000; // 2 seconds
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        const streak = await fetchStreakAndWData();

        if (streak > 0) {
          await fetchPerformanceData();
          break;
        } else {
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }

      if (retryCount === maxRetries) {
        console.error('Failed to fetch streak data after maximum retries.');
      }
    };
    fetchData();
  }, [playerData]);

  const handleShowLeaderboard = () => {
    setShowLeaderboard(true);
    setTimeout(() => setAnimate(true), 0);
  };

  const handleCloseLeaderboard = () => {
    setAnimate(false);
    setTimeout(() => setShowLeaderboard(false), 500);
  };

  const handleShareResult = async () => {
    if (resultRef.current && buttonRef.current) {
      buttonRef.current.style.display = 'none'; // Hide buttons before capture
  
      try {
        const canvas = await html2canvas(resultRef.current, { scale: 2, useCORS: true });
        const ctx = canvas.getContext('2d');
        const { width, height } = canvas;
  
        const scaleFactor = 2;
        const newWidth = width * scaleFactor;
        const newHeight = height * scaleFactor + 500;
        const newCanvas = document.createElement('canvas');
        newCanvas.width = newWidth;
        newCanvas.height = newHeight;
        const newCtx = newCanvas.getContext('2d');
  
        newCtx.scale(scaleFactor, scaleFactor);
        newCtx.drawImage(canvas, 0, 0);
  
        newCtx.scale(1 / scaleFactor, 1 / scaleFactor);
        newCtx.fillStyle = '#f8f9fa';
        newCtx.fillRect(0, height * scaleFactor, newWidth, 500);
  
        newCtx.font = 'bold 48px Arial';
        newCtx.fillStyle = '#333';
        newCtx.textAlign = 'center';
        newCtx.textBaseline = 'middle';
  
        const textY = height * scaleFactor + 200;
        const headline = 'Can you beat my quiz score and top the leaderboard?';
        const maxTextWidth = newWidth - 80;
        const textLines = splitTextIntoLines(headline, newCtx, maxTextWidth);
  
        textLines.forEach((line, index) => {
          newCtx.fillText(line, newWidth / 2, textY - (textLines.length - index - 1) * 60);
        });
  
        const img = new Image();
        img.src = 'image/eduplayzone-badge-1.png';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
  
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;
  
        const padding = 40;
        const maxImgWidth = 350;
        const maxImgHeight = 350;
        const scale = Math.min(maxImgWidth / imgWidth, maxImgHeight / imgHeight);
        const scaledWidth = imgWidth * scale;
        const scaledHeight = imgHeight * scale;
        const x = (newWidth - scaledWidth) / 2;
        const y = height * scaleFactor + (650 - scaledHeight) / 2;
  
        newCtx.drawImage(img, x, y, scaledWidth, scaledHeight);
  
        newCtx.font = 'italic 36px Arial';
        newCtx.fillStyle = '#333';
        newCtx.textAlign = 'center';
        newCtx.fillText('Play today at: www.eduplayzone.in', newWidth / 2, newHeight - 40);
  
        newCanvas.toBlob(async (blob) => {
          try {
            const file = new File([blob], 'result.png', { type: 'image/png' });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: 'Check out my quiz score!',
                text: 'Can you beat my quiz score and top the leaderboard?'
              });
            } else {
              // Handle cases where file sharing is not supported
              alert('Sharing files is not supported on this device or browser.');
            }
          } catch (error) {
            console.error('Error sharing image:', error);
            alert('Failed to share the image.');
          }
          buttonRef.current.style.display = 'flex'; // Show buttons after capture
        }, 'image/png');
      } catch (error) {
        console.error('Error capturing or processing screenshot:', error);
        buttonRef.current.style.display = 'flex';
      }
    }
  };
  
  
  // Helper function to split text into lines to prevent overflow
  const splitTextIntoLines = (text, ctx, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) lines.push(currentLine);

    return lines;
  };

  const { score, total, points } = performanceData;
  const percentage = ((score / total) * 100).toFixed(0);

  // const handleCloseNotificationPopup = () => {
  //   setShowNotificationPopup(false);
  // };

  // const handleShowCertificate = () => {
  //   setShowCertificate(true);
  // };

  // const handleHideCertificate = () => {
  //   setShowCertificate(false);
  // };

  const imagesAndLinks = [
    { url: 'https://eduplayzone.in/image/ads/follow-insta-epz.png', link: 'https://www.instagram.com/eduplayzone/' },
    { url: 'https://eduplayzone.in/image/ads/craft-race.png', link: 'https://play.google.com/store/apps/details?id=com.gameerector.CraftRace' },
    { url: 'https://eduplayzone.in/image/ads/train-tail.png', link: 'https://play.google.com/store/apps/details?id=com.Gameerector.TrainTail' },
    { url: 'https://eduplayzone.in/image/ads/csp.png', link: 'https://play.google.com/store/apps/details?id=com.GameErector.ColorSplatPuzzle' },
    // Add more images and links as needed
  ];
  const [randomImage, setRandomImage] = useState(null);
  
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * imagesAndLinks.length);
    setRandomImage(imagesAndLinks[randomIndex]);
  }, []); // Runs only on mount

  return (
    <div>
      <NavigationBar playerData={playerData} />
      <Container className="d-flex flex-column align-items-center" style={{ paddingTop: '5px' }}>
        <Card className="text-center result-card mb-4" ref={resultRef}>
          <Card.Body>
            <img 
              height={'100px'} // Increased size for better visibility
              src={playerData.avatar}
              alt="Player"
              className="player-image-result"
            />

            <h5 className="mb-1">{getAppreciationMessage(score)}</h5>
            <h4 className='player-name-r'>{playerData.name}</h4>
            {rank !== null && <span className='rankText'>You got the {getOrdinal(rank)} Place in the quiz</span>}
            <div className='box-score mb-2 mt-2'>
              <div className='score-sec'>
              <CircularProgressbar
              value={performanceData.score}
              maxValue={performanceData.total}
              circleRatio={0.94}
              strokeWidth={12} // Adjust this value to increase the width of the progress bar
              styles={buildStyles({
                textSize: '16px',
                pathColor: '#2bf550',
                textColor: '#333',
                trailColor: '#00000000',
              })}
              />
              <i class="fa-solid fa-check icon-res score-c"></i>
                <h4>{score}/{total}</h4>
                <span>Scored</span>
              </div>
              <div className='score-sec'>
              <CircularProgressbar
              value={performanceData.points}
              maxValue={100}
              circleRatio={0.94}
              strokeWidth={12} // Adjust this value to increase the width of the progress bar
              styles={buildStyles({
                textSize: '16px',
                pathColor: '#fd9974',
                textColor: '#333',
                trailColor: '#00000000',
              })}
              />
              <i class="fa-solid fa-star icon-res point-c"></i>                
                <h4>{points}</h4>
                <span>Points</span>
              </div>
              <div className='score-sec'>
              <CircularProgressbar
              value={percentage}
              maxValue={100}
              circleRatio={0.94}
              strokeWidth={12} // Adjust this value to increase the width of the progress bar
              styles={buildStyles({
                textSize: '16px',
                pathColor: '#ffdc00',
                textColor: '#333',
                trailColor: '#00000000',
              })}
              />
                <i class="fa-solid fa-percent icon-res"></i>                
                <h4>{percentage}%</h4>
                <span>Percentage</span>
              </div>
            </div>
            
            {/* show the quiz played day streak */}
            <div className="rpb">
            <h5 className='newD-text'>Keep it up <span>New quiz daily</span></h5>
            <span className='streakText'>{streak}-Day played streak</span>
          </div>

          <div className="rpb week">
          <ul>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <li key={index} className="p-day">
                  {day}
                  <span className={weekPlayed[index] ? 'dayPlayed' : ''}>
                    {weekPlayed[index] && <i className="fa-solid fa-check"></i>}
                  </span>
                </li>
              ))}
            </ul>
            <div className="lineWeek"></div>
          </div>  

            <ProgressBar style={{display:'none'}} now={percentage} label={`${percentage}%`} className="mb-4" />
            <div className='button-box-result' ref={buttonRef}>
              <div className='button-h-s'>
                <Button  
                  variant="primary" 
                  onClick={() => navigate('/')}
                >
                  <i class="fa-solid fa-house"></i>
                </Button>
                <Button 
                variant="secondary" 
                className="mt-3" 
                onClick={handleShowLeaderboard}
              >
                Leaderboard
              </Button>
                <Button variant="primary" className="mt-3 share-btn" onClick={handleShareResult}><i className="fa-solid fa-share-nodes"></i>   Share</Button>
              </div>
            </div>
            {/* start-  ads container for own ads */}
           {randomImage && (
              <div style={{ margin: '2px 0', textAlign: 'center' }}>
                <a href={randomImage.link} target="_blank" rel="noopener noreferrer">
                  <img src={randomImage.url} className='own-game-ad' alt="Random Game" style={{ maxWidth: '100%', height: 'auto' }} />
                </a>
              </div>
            )}

            {/* end-  ads container for own ads */}
          
          </Card.Body>
        </Card>
        <AdsContainer />
      {/* Lazy load MoreGame component */}
      <Suspense fallback={<div>Loading games...</div>}>
        <MoreGame />
      </Suspense>
         {/* <div className='certificate-section'>
            {showCertificate && (
            <Suspense fallback={<h5>Your certificate is being generated. Please wait...</h5>}>
              <Certificate playerName={playerData.name} quizTitle={quizTitle} />
            </Suspense>
          )}
         </div> */}

        {showLeaderboard && (
          <div className="leaderboard-overlay">
            <Leaderboard onClose={handleCloseLeaderboard} animate={animate} quizDate={quizDate} playerData={playerData} />
          </div>
        )}
      {showNotificationPopup && (
        <NotificationPopup
          show={showNotificationPopup}
          handleClose={() => setShowNotificationPopup(false)}
          requestPermission={requestPermission}
        />
      )}
      </Container>
    </div>
  );
};

// const certificateStyles = {
//   overlay: {
//     position: 'fixed',
//     top: 0,
//     left: 0,
//     width: '100%',
//     height: '100%',
//     backgroundColor: 'rgba(0, 0, 0, 0.5)',
//     display: 'flex',
//     justifyContent: 'center',
//     alignItems: 'center',
//     zIndex: 1000
//   },
//   closeButton: {
//     position: 'absolute',
//     top: '10px',
//     right: '10px'
//   }
// };



export default Result;
